import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DateInput } from 'semantic-ui-calendar-react'
import { Button, Form, Header, Segment, Table } from 'semantic-ui-react'
import ProfileSettingsService from '~/api/services/profileSettings'
import { MilkType } from '~/api/services/reporting'
import CustomIcon from '~/components/CustomIcon'
import Dialog from '~/components/Dialog'
import { FormFields } from '~/components/FormFields'
import { getError } from '~/components/FormFields/util'
import notify from '~/components/Notify'
import TwoFaComp, { LabelTwoFaType } from '~/components/TwoFaComp'
import { useAuth } from '~/routes/components/AuthProvider'
import { getDayAhead, handleErrors } from '~/utils/helpers'

interface Props {
  openGeneratingModal: boolean
  handleClose: () => void
  handleGenerateInvoice: (data?) => void
  milkTypes: MilkType[]
  generating: boolean
}

const GenerateModal: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const { systemData, systemData: { languages, logged: { languageId } } } = useAuth()
  const { openGeneratingModal, handleClose, handleGenerateInvoice, milkTypes, generating } = props
  const tfaGenerateMga = systemData?.roleSettings?.tfaGenerateMga === 1
  const tfaType = systemData?.logged?.tfaType
  const languageLocal = languages.find(language => language.id === languageId)

  const [isBusy, setIsBusy] = useState(false)
  const [correctTfaCode, setCorrectTfaCode] = useState(false)

  const { control, setValue, formState: { isSubmitted }, handleSubmit, getValues } = useForm({
    mode: 'onSubmit'
  })

  const watchDate = useWatch({ control, name: 'date' })
  const watchDateFrom = useWatch({ control, name: 'dateFrom' })
  const watchDateTo = useWatch({ control, name: 'dateTo' })

  const { fields: milkTypesArray, remove, append } = useFieldArray({
    control,
    name: 'milkTypes',
    keyName: 'key'
  })

  useEffect(() => {
    const milkTypes = getValues('milkTypes') || []
    if (tfaGenerateMga && openGeneratingModal) {
      handleSendCode()
    }
    if (openGeneratingModal && milkTypes.length === 0) {
      append({
        id: null,
        price: ''
      })
    }
  }, [openGeneratingModal])

  async function handleSendCode() {
    if (tfaType === 'email' || tfaType === 'sms') {
      setIsBusy(true)
      try {
        await ProfileSettingsService.sendCode()
        notify(t('Code sent'), 'success')
      } catch (error) {
        handleErrors(error)
      }
      setIsBusy(false)
    }
  }

  function renderMilkTypes() {
    return <div style={{ marginTop: '1em' }}>
      <Header attached={'top'}>{'Preise für MG-Nachzahlung'}</Header>
      <Segment attached>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t('Milk type')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Price (in € Ct/kg)')}</Table.HeaderCell>
              <Table.HeaderCell width={2}>{t('Actions')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {milkTypesArray.map((milkType, index) => {
              return (
                <Table.Row key={milkType.key}>
                  <Table.Cell>
                    <FormFields.Select
                      rules={{ required: true }}
                      upward
                      withoutAsterisk
                      noErrorMessage
                      scroling
                      name={`milkTypes[${index}].id`}
                      control={control}
                      placeholder={t('Select type of milk')}
                      options={milkTypes?.map(milkType => ({ text: `${milkType.name} (${milkType.abbreviation})`, value: milkType.id })) || []}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <FormFields.Input
                      control={control}
                      name={`milkTypes[${index}].price`}
                      withoutAsterisk
                      noErrorMessage
                      placeholder={'Preis (in € Ct/kg) festlegen'}
                      typeInput='controled'
                      decimalScale={2}
                      allowDecimals
                      rules={{
                        required: true
                      }}
                    />
                  </Table.Cell>
                  <Table.Cell textAlign='center'>
                    {milkTypesArray.length > 1 && <CustomIcon
                      name={'trash'}
                      style={{ fontSize: '1.3em', color: 'red' }}
                      onClick={() => remove(index)}
                    />}
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table>
        <div style={{ display: 'flex', justifyContent: 'end', marginTop: '1em' }}>
          <Button
            type={'button'}
            onClick={() => append({
              id: null,
              price: ''
            })}
            icon={'plus'}
            style={{ color: 'white' }}
          >{t('Create milk type')}</Button>
        </div>
      </Segment>
    </div>
  }

  function renderContent() {
    const previousYear = dayjs().year() - 1
    const minDateFrom = `01.01.${previousYear}`
    const maxDateFrom = `${dayjs().format('DD.MM.YYYY')}`
    const minDateTo = watchDateFrom ? getDayAhead(watchDateFrom) : minDateFrom
    const todaysDate = dayjs(new Date()).format('DD.MM.YYYY')

    return <Form id='generate-modal-form' onSubmit={handleSubmit(onSubmit)}>
      <Form.Group widths={'equal'}>
        <FormFields.Input
          rules={{ required: { value: true, message: t('Please enter name') } }}
          control={control}
          label={'Titel MG-Nachzahlung'}
          name={'name'}
          placeholder={'Titel für MG-Nachzahlung definieren'}
        />
        <Controller
          control={control}
          name={'date'}
          rules={{
            required: {
              value: true,
              message: t('Please enter date')
            }
          }}
          render={({ field, fieldState }) => {
            return <Form.Field
              required
              error={!watchDate && isSubmitted ? getError(fieldState) : false}
            >
              <label>{'Abrechnungsdatum'}</label>
              <DateInput
                name='date'
                placeholder={'Abrechnungsdatum auswählen'}
                closable
                onKeyDown={(event) => {
                  event.preventDefault();
                }}
                markColor={'blue'}
                minDate={todaysDate}
                initialDate={todaysDate}
                localization={languageLocal.code}
                closeOnMouseLeave={false}
                dateFormat={'DD.MM.YYYY'}
                className={'datePicker'}
                value={watchDate}
                iconPosition='left'
                onChange={(event, { name, value }) => setValue('date', value)}
              />
            </Form.Field>

          }}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        <Controller
          control={control}
          name={'dateFrom'}
          rules={{
            required: {
              value: true,
              message: t('Please enter period from')
            }
          }}
          render={({ field, fieldState }) => {
            return <Form.Field
              required
              error={!watchDateFrom && isSubmitted ? getError(fieldState) : false}
            >
              <label>{'Zeitraum Milchanlieferungen von'}</label>
              <DateInput
                name='date'
                placeholder={'Datum auswählen'}
                minDate={minDateFrom}
                maxDate={maxDateFrom}
                closable
                onKeyDown={(event) => {
                  event.preventDefault();
                }}
                markColor={'blue'}
                localization={languageLocal.code}
                closeOnMouseLeave={false}
                dateFormat={'DD.MM.YYYY'}
                className={'datePicker'}
                value={watchDateFrom}
                iconPosition='left'
                onChange={(event, { name, value }) => setValue('dateFrom', value)}
              />
            </Form.Field>

          }}
        />

        <Controller
          control={control}
          name={'dateTo'}
          rules={{
            required: {
              value: true,
              message: t('Please enter period to')
            }
          }}
          render={({ field, fieldState }) => {
            return <Form.Field
              required
              error={!watchDateFrom && isSubmitted ? getError(fieldState) : false}
            >
              <label>{'Zeitraum Milchanlieferungen bis'}</label>
              <DateInput
                name='date'
                placeholder={'Datum auswählen'}
                minDate={minDateTo}
                maxDate={maxDateFrom}
                disabled={!watchDateFrom}
                closable
                onKeyDown={(event) => event.preventDefault()}
                markColor={'blue'}
                localization={languageLocal.code}
                closeOnMouseLeave={false}
                dateFormat={'DD.MM.YYYY'}
                className={'datePicker'}
                value={watchDateTo}
                iconPosition='left'
                onChange={(event, { name, value }) => setValue('dateTo', value)}
              />
            </Form.Field>
          }}
        />
      </Form.Group>

      {renderMilkTypes()}

      {tfaGenerateMga && <Controller
        control={control}
        name={'code'}
        rules={{
          required: {
            value: true,
            message: t('Please enter code')
          }
        }}
        render={({ field, fieldState }) => {
          return <TwoFaComp
            onChangeTfaCode={(code) => setValue('code', code)}
            error={Boolean(fieldState?.error)}
            type={LabelTwoFaType.Top}
            disableMarginTop
            onChangeCorrectTfaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
          />
        }}
      />
      }
    </Form>
  }

  async function onSubmit(data) {
    handleGenerateInvoice(data)
  }

  return <Dialog
    isDialogOpen={openGeneratingModal}
    handleClose={() => handleClose()}
    textHeader={t('Process start: Generate additional payment')}
    typeButton='submit'
    formId='generate-modal-form'
    textSubmit={t('Generate additional payment')}
    disableSubmit={tfaGenerateMga ? !correctTfaCode : false}
    isLoading={isBusy || generating}
    textClose={t('Cancel')}
    textContent={renderContent()}
  />
}
export default GenerateModal